import styled, { createGlobalStyle } from 'styled-components';

export const DatePickerDarkStyles = createGlobalStyle`
  .react-datepicker {
    background-color: var(--cco-surface);
    border-color: var(--cco-line);
  }
  .react-datepicker__header {
    background-color: var(--cco-surface);
    border-bottom-color: var(--cco-line);
  }
  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    color: ${(props) =>
      props.theme.title === 'light'
        ? 'var(--cco-ink)'
        : props.theme.colors.white};
  }
  .react-datepicker__day:hover {
    background-color: var(--cco-line);
  }
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: var(--cco-blue);
    color: ${(props) => props.theme.colors.white};
  }
  .react-datepicker__day--disabled {
    color: var(--cco-ink-mute);
    opacity: 0.5;
  }
  .react-datepicker__navigation-icon::before {
    border-color: ${(props) =>
      props.theme.title === 'light'
        ? 'var(--cco-ink)'
        : props.theme.colors.white};
  }
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before,
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::after {
    border-bottom-color: var(--cco-surface);
  }
`;

// Container — single panel estilo X/Twitter (sem cards individuais).
// Borda externa hairline, sections internas separadas por border-top.
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 64px);
  min-height: 100vh;
  margin: 0 0 0 64px;
  align-items: stretch;
  gap: 0;
  padding: 0;
  box-sizing: border-box;
  background: var(--cco-bg);
  color: var(--cco-ink);

  > section + section {
    border-top: 1px solid var(--cco-line);
  }

  @media (max-width: 720px) {
    width: 100vw;
    margin: 0;
  }
`;

// Header — flush, sem card, titulo + subtitulo direto na superficie.
export const PageHeader = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 22px var(--cco-pad) 24px;
  background: transparent;
  border: none;
  border-radius: 0;
  width: 100%;
  box-sizing: border-box;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: var(--fs-heading);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--cco-ink);
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--cco-ink-mute);
  line-height: 1.5;
  max-width: 760px;
`;

// Body: grid 2-col (form 1fr | sidebar 380px) com hairline vertical
// entre as duas colunas. Sem gap interno. width 100% + box-sizing
// border-box garante que o grid nao extrapola o Container.
export const MainGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 0;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;

  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    > * + * {
      border-left: none;
      border-top: 1px solid var(--cco-line);
    }
  }
`;

export const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

export const SidebarColumn = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  /* Hairline divider entre Section blocks (ex.: Templates ↔ Histórico).
     Mesma estrategia das Section principais do Container — Section nao
     define borda propria, divisao vem do pai via combinator > + >. */
  > section + section {
    border-top: 1px solid var(--cco-line);
  }
`;

// Breadcrumb: flush no topo (sem card), 14px padding vertical
// + 24px lateral conforme design.
export const BreadcrumbWrapper = styled.section`
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 14px var(--cco-pad);
`;

// Section: cada passo (Destino/Periodo/Variaveis) vira uma Section.
// Sem card individual — divisao entre sections vem do Container via
// `> section + section { border-top }`. Section nao define borda
// propria pra nao criar hairline duplo (sobrepondo a do Container).
export const Section = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
  min-height: 0;
  padding: 24px var(--cco-pad);
  gap: 14px;

  @media (max-width: 480px) {
    padding: 16px var(--cco-pad);
  }
`;

// SectionTag (chip do step): pill 999px com soft-blue bg + inset border
// blue, contendo numero em circulo + texto. Match exato do design
// Relatorios.html.
export const SectionTag = styled.div`
  align-self: flex-start;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 6px;
  border-radius: 999px;
  background: var(--cco-blue-soft);
  color: var(--cco-blue);
  box-shadow: inset 0 0 0 1px rgba(78, 161, 243, 0.25);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  flex-shrink: 0;
  pointer-events: none;
`;

// Numero do step (1, 2, 3) — circulo solido blue 20x20 com texto dark.
export const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--cco-blue);
  color: #0a0a0b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`;

// SectionContent: o Section pai ja tem padding 24px var(--cco-pad) — aqui
// so controla layout interno (flex column + gap).
export const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
`;

// Cabecalho do step: chip + descricao na MESMA linha (match design).
export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

// Descricao do step — fica inline ao lado da chip, cor mute, 13.5px.
export const SectionHint = styled.p`
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--cco-ink-mute);
  line-height: 1.5;
  flex: 1;
  min-width: 200px;
`;

// FieldsRow: grid 3-col com gap generoso (20px) — sem isso o olho lia
// as 3 celulas com bordas hairline como um bloco unico com divisores
// internos. Boxes precisam respirar pra parecerem cards separados.
export const FieldsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

// Field: cada celula com sua propria borda hairline, radius 12,
// padding generoso. Container do label + control. box-sizing border-box
// pra que o padding nao extrapole a celula 1fr do grid (sem isso o
// chevron do react-select aparecia sobre o box vizinho).
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 16px 18px;
  border: 1px solid var(--cco-line);
  border-radius: 12px;
  background: transparent;
  overflow: hidden;
`;

// Label: 10.5px, weight 600, letter-spacing 0.14em — match design exato.
export const FieldLabel = styled.label`
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--cco-ink-mute);
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 5px;

  ${(props) =>
    props.required &&
    `&::after {
      content: '*';
      color: var(--cco-red);
    }`}
`;

// Linha de chips de preset rapido pra periodos comuns. Operador clica e
// os dois date pickers ja sao configurados. Atalho mais rapido do que
// abrir 2 calendarios. Padrao do redesign (chips de filtro).
export const PresetChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;

export const PresetChipsLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--cco-ink-mute);
  opacity: 0.75;
  margin-right: 4px;
`;

export const PresetChip = styled.button`
  padding: 4px 12px;
  height: 26px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: all 120ms ease;
  border: 1px solid
    ${(props) =>
      props['aria-pressed'] === 'true'
        ? 'var(--cco-blue)'
        : 'var(--cco-line)'};
  background: ${(props) =>
    props['aria-pressed'] === 'true'
      ? 'var(--cco-blue)'
      : 'transparent'};
  color: ${(props) =>
    props['aria-pressed'] === 'true'
      ? props.theme.colors.white
      : 'var(--cco-ink-mute)'};

  :hover {
    border-color: var(--cco-blue);
    color: ${(props) =>
      props['aria-pressed'] === 'true'
        ? props.theme.colors.white
        : 'var(--cco-blue)'};
  }
`;

// Hint pequeno abaixo do multi-select de variaveis — explica como interagir.
export const HintLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--cco-ink-mute);
  opacity: 0.7;
  font-style: italic;
  margin-top: 2px;
`;

// Footer com botoes — alinhado a direita, separado do conteudo por divider.
// Footer — padding lateral var(--cco-pad) pra alinhar os botoes
// (LIMPAR CAMPOS, GERAR RELATORIO) com os inputs das sections acima.
// Sem isso o Footer ficava colado nas bordas da FormColumn enquanto
// os inputs respeitavam o padding 24px das Sections.
export const Footer = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  margin-top: auto;
  padding: 18px var(--cco-pad) 24px;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    padding: 12px var(--cco-pad);
  }
`;

// Botao limpar — outline vermelho discreto. border-radius alinhado ao
// padrao 10-12px do app (era 16px gigante).
export const LimparButton = styled.button`
  margin-right: auto;
  padding: 8px 16px;
  border-radius: 6px;
  color: var(--cco-red);
  background: transparent;
  cursor: pointer;
  text-align: center;
  transition: all 180ms ease;
  outline: none;
  border: 1px solid var(--cco-red);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-family: inherit;

  :hover {
    color: ${(props) => props.theme.colors.white};
    background: var(--cco-red);
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

// Botao primario — Gerar relatorio. Mesmo padrao + size maior (CTA primario).
export const Button = styled.button`
  padding: 10px 22px;
  border-radius: 6px;
  color: ${(props) => props.theme.colors.white};
  background: var(--cco-blue);
  cursor: pointer;
  text-align: center;
  transition: all 180ms ease;
  outline: none;
  border: 1px solid var(--cco-blue);
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: inherit;

  :hover:not(:disabled) {
    background: ${(props) => props.theme.colors.blue};
    border-color: ${(props) => props.theme.colors.blue};
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

// Botao do DatePicker — herda look dos campos do form (border + radius).
// ButtonDate — usado dentro de Field bordered, entao sem borda propria
// nem padding (matching o pattern do react-select control). Apenas
// texto da data alinhado a esquerda, sem caixa interna duplicada.
export const ButtonDate = styled.button`
  cursor: pointer;
  width: 100%;
  min-height: 28px;
  padding: 0;
  border: none;
  border-radius: 0;
  background-color: transparent;
  color: ${(props) =>
    props.theme.title === 'light'
      ? 'var(--cco-ink)'
      : props.theme.colors.white};
  text-align: left;
  font-size: 14.5px;
  font-weight: 500;
  font-family: inherit;
  outline: none;

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Pill discreto ao lado do botao 'Gerar' indicando quantos campos
// faltam. Mostra hover com a lista completa. Custo visual baixo —
// mensagem clara em vez do botao silencioso disabled.
export const PendingPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  height: 26px;
  border-radius: 8px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(246, 222, 96, 0.18)'
      : 'rgba(246, 222, 96, 0.20)'};
  border: 1px solid var(--cco-yellow);
  color: var(--cco-yellow);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: help;
  white-space: nowrap;
`;

// Loader overlay — substitui o Loader bruto que escondia os botoes inteiros.
// Mostra spinner + texto de status durante geracao do relatorio.
export const LoaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 13px;
  color: var(--cco-ink-mute);
`;

// Lista de presets/historico — items pequenos clicaveis. Padrao reusado
// entre as duas secoes (Presets e Historico).
export const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0, 0, 0, 0.02)'
      : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid var(--cco-line);
  transition: background 120ms ease, border-color 120ms ease;

  :hover {
    border-color: var(--cco-blue);
  }
`;

export const ItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const ItemTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: var(--cco-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ItemMeta = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--cco-ink-mute);
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

// Botoes secundarios pequenos (Carregar, Apagar) usados nas listas.
export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 120ms ease;
  border: 1px solid
    ${(props) =>
      props.danger ? 'var(--cco-red)' : 'var(--cco-blue)'};
  color: ${(props) =>
    props.danger ? 'var(--cco-red)' : 'var(--cco-blue)'};

  :hover {
    background: ${(props) =>
      props.danger ? 'var(--cco-red)' : 'var(--cco-blue)'};
    color: ${(props) => props.theme.colors.white};
  }
`;

// Empty state padronizado pra listas vazias (sem presets / sem historico).
export const EmptyHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-size: 12px;
  color: var(--cco-ink-mute);
  font-style: italic;
  opacity: 0.75;
  text-align: center;
`;

// Linha de campo "Salvar preset com nome" — input + botao inline.
export const SavePresetRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  input {
    flex: 1;
    min-width: 0;
    height: 36px;
    padding: 0 12px;
    border-radius: 6px;
    border: 1px solid var(--cco-line);
    background: transparent;
    color: ${(props) =>
      props.theme.title === 'light'
        ? 'var(--cco-ink)'
        : props.theme.colors.white};
    font-family: inherit;
    font-size: 13px;
    outline: none;
    transition: border-color 150ms ease;

    :focus {
      border-color: var(--cco-blue);
    }

    ::placeholder {
      color: var(--cco-ink-mute);
      opacity: 0.6;
    }
  }
`;

// Base do estilo do react-select customizado. Padrao do sistema:
// control transparent sem borda (o Field pai ja tem borda), texto
// alinhado a esquerda em 14.5px, padding minimal — fica como uma
// linha de selecao dentro da celula bordered.
const baseSelectStyles = (theme) => ({
  container: (provided) => ({
    ...provided,
    width: '100%',
  }),
  option: (provided, state) => ({
    ...provided,
    padding: '8px 12px',
    fontSize: 13,
    cursor: 'pointer',
    backgroundColor: state.isSelected
      ? theme.colors.lightblue
      : state.isFocused
      ? theme.colors.lightergrey
      : theme.colors.card,
    color:
      theme.title === 'light' ? theme.colors.darkerblue : theme.colors.white,
  }),
  control: (provided, state) => ({
    ...provided,
    width: '100%',
    minHeight: 28,
    borderRadius: 0,
    backgroundColor: 'transparent',
    border: 'none',
    borderColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
    cursor: 'pointer',
    ':hover': { borderColor: 'transparent' },
    opacity: state.isDisabled ? 0.5 : 1,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  singleValue: (provided, state) => ({
    ...provided,
    opacity: state.isDisabled ? 0.5 : 1,
    transition: 'opacity 300ms',
    margin: 0,
    fontSize: 14.5,
    fontWeight: 500,
    color:
      theme.title === 'light' ? theme.colors.darkerblue : theme.colors.white,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1001,
    backgroundColor: theme.colors.card,
    border: `1px solid ${theme.colors.disabled}`,
    borderRadius: 10,
    overflow: 'hidden',
  }),
  // menuPortal: renderiza o menu fora da Section (que tem overflow:
  // hidden) via portal no body. z-index alto pra ficar acima de tudo.
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 14.5,
    fontWeight: 500,
    color: theme.colors.label,
    opacity: 0.65,
    margin: 0,
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    color:
      theme.title === 'light' ? theme.colors.darkerblue : theme.colors.white,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: theme.colors.lightgrey,
    borderRadius: 8,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color:
      theme.title === 'light' ? theme.colors.darkerblue : theme.colors.white,
    fontSize: 12,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color:
      theme.title === 'light' ? theme.colors.darkerblue : theme.colors.white,
    ':hover': {
      backgroundColor: theme.colors.red,
      color: theme.colors.white,
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorContainer: () => ({
    padding: 5,
  }),
});

export const customStyles = baseSelectStyles;

// Multi-select de variaveis — usa option groups (Variaveis | Setpoints).
// groupHeading destacado: weight 800 uppercase com cor brand, separa
// claramente as duas categorias dentro do mesmo dropdown.
export const customStylesVariables = (theme) => ({
  ...baseSelectStyles(theme),
  group: (provided) => ({
    ...provided,
    paddingTop: 4,
    paddingBottom: 4,
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontSize: 9,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: theme.colors.lightblue,
    padding: '6px 12px 4px',
    marginBottom: 2,
    borderBottom: `1px solid ${theme.colors.disabled}`,
    background:
      theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.02)'
        : 'rgba(255, 255, 255, 0.03)',
  }),
});
